import React, {FC, Key, ReactNode, useLayoutEffect, useRef} from 'react'
import {
  buildPageSiblings,
  getPageSiblingRank,
  type MappingNode,
  type PageSibling,
  type SettingNode,
} from 'src/utils/channelSettingsSequence'
import {
  collapsePairedMappingSiblings,
  lookupSequenceNode,
  type SequenceNodeRegistry,
} from 'src/utils/channelSettingSequenceRegistry'
import {
  CHANNEL_SETTING_SEQUENCE_ROW_CONTAINER_CLASS,
  ChannelSettingSequenceRowSpan,
  getChildSettingSequenceRowSpan,
  getMappingSequenceRowSpan,
} from 'src/utils/channelSettingSequenceLayout'
import {
  buildSequenceRenderItems,
  expandSequencePresentationSlots,
  type SequenceAlignmentPlan,
} from 'src/utils/channelSettingSequencePresentation'
import {isSettingFileChildInputType} from 'src/utils/settingFileFieldMappings'
import ChannelSettingSequencePair from './ChannelSettingSequencePair'
import {withChannelSettingSequence} from './ChannelSettingSequenceSlot'

type ChannelSettingSequenceRowProps = {
  siblingRanks: Map<string, number>
  nodeByKey: SequenceNodeRegistry
  thisSetting: SettingNode | null | undefined
  extraChildren?: SettingNode[]
  otherSetting?: SettingNode | null
  getRowSpan?: (sibling: PageSibling) => ChannelSettingSequenceRowSpan
}

const defaultRowSpan = (sibling: PageSibling): ChannelSettingSequenceRowSpan => {
  if (sibling.kind === 'child') {
    return getChildSettingSequenceRowSpan(sibling.item as SettingNode)
  }

  return getMappingSequenceRowSpan(sibling.item as MappingNode)
}

const ChannelSettingSequenceRow: FC<ChannelSettingSequenceRowProps> = ({
  siblingRanks,
  nodeByKey,
  thisSetting,
  extraChildren = [],
  otherSetting,
  getRowSpan = defaultRowSpan,
}) => {
  const linkedMappingAnchorRefs = useRef(new Map<string, React.RefObject<HTMLDivElement>>())
  const linkedBooleanRefs = useRef(
    new Map<
      string,
      {parentToggle: React.RefObject<HTMLDivElement>; childPanel: React.RefObject<HTMLDivElement>}
    >()
  )
  const retainedNestedPanelKeys = useRef(new Set<string>())

  const getLinkedMappingAnchorRef = (slotKey: string): React.RefObject<HTMLDivElement> => {
    let anchorRef = linkedMappingAnchorRefs.current.get(slotKey)
    if (!anchorRef) {
      anchorRef = React.createRef<HTMLDivElement>()
      linkedMappingAnchorRefs.current.set(slotKey, anchorRef)
    }
    return anchorRef
  }

  const getLinkedBooleanRefs = (slotKey: string) => {
    let refs = linkedBooleanRefs.current.get(slotKey)
    if (!refs) {
      refs = {
        parentToggle: React.createRef<HTMLDivElement>(),
        childPanel: React.createRef<HTMLDivElement>(),
      }
      linkedBooleanRefs.current.set(slotKey, refs)
    }
    return refs
  }

  const slots = collapsePairedMappingSiblings(
    buildPageSiblings({thisSetting, extraChildren, otherSetting})
  )

  const alignmentPlans: SequenceAlignmentPlan[] = []
  const visibleNestedPanelKeys: string[] = []

  slots.forEach((sibling) => {
    const node = lookupSequenceNode(nodeByKey, sibling)
    if (node == null || node === false) {
      return
    }

    const rank = getPageSiblingRank(
      siblingRanks,
      sibling.kind,
      sibling.source,
      sibling.item
    )
    const slotKey: Key = `${sibling.kind}:${sibling.source}:${
      sibling.kind === 'child'
        ? (sibling.item as SettingNode).id ?? (sibling.item as SettingNode).name
        : (sibling.item as {setting_mapping_id?: number; api_code?: string}).setting_mapping_id ??
          (sibling.item as {api_code?: string}).api_code
    }`
    const rowSpan = getRowSpan(sibling)
    const childItem = sibling.kind === 'child' ? (sibling.item as SettingNode) : null
    const hasNestedBooleanContent =
      childItem?.input_type === 'BOOLEAN' &&
      (Boolean(childItem.children?.length) || Boolean(childItem.field_mappings?.length))
    const booleanRefs = hasNestedBooleanContent
      ? getLinkedBooleanRefs(String(slotKey))
      : null

    expandSequencePresentationSlots(node, String(slotKey), rowSpan, {
      linkedMappingAnchorRef: getLinkedMappingAnchorRef(String(slotKey)),
      linkedParentToggleRef: booleanRefs?.parentToggle,
      linkedChildPanelRef: booleanRefs?.childPanel,
      retainNestedPanel: retainedNestedPanelKeys.current.has(`${slotKey}-panel`),
    }).forEach((slot) => {
      if (slot.isPanel && !slot.isHidden) {
        visibleNestedPanelKeys.push(slot.key)
      }
      alignmentPlans.push({
        ...slot,
        rank: rank ?? 0,
      })
    })
  })

  useLayoutEffect(() => {
    visibleNestedPanelKeys.forEach((key) => retainedNestedPanelKeys.current.add(key))
  })

  const hiddenNestedPanels = alignmentPlans.filter((plan) => plan.isPanel && plan.isHidden)
  const rendered = buildSequenceRenderItems(
    alignmentPlans.filter((plan) => !plan.isHidden)
  ).map((item) => {
    if (item.type === 'pair') {
      return (
        <ChannelSettingSequencePair
          key={item.key}
          pairKey={item.key}
          order={item.rank}
          left={item.left.content}
          right={item.right.content}
        />
      )
    }

    if (item.type === 'full') {
      return withChannelSettingSequence(item.rank, item.key, item.content, {rowSpan: 'full'})
    }

    return withChannelSettingSequence(item.rank, item.key, item.content, {rowSpan: 'half'})
  })
  hiddenNestedPanels.forEach((panel) => {
    rendered.push(
      withChannelSettingSequence(panel.rank, panel.key, panel.content, {
        rowSpan: 'full',
        hidden: true,
      })
    )
  })

  if (!rendered.length) {
    return null
  }

  return <div className={CHANNEL_SETTING_SEQUENCE_ROW_CONTAINER_CLASS}>{rendered}</div>
}

/** Legacy Row A wrapper; skipped when the unified sequence row owns layout. */
export const ChannelSettingLegacySequenceContainer: FC<{
  enabled: boolean
  className?: string
  children: ReactNode
}> = ({enabled, className = 'row p-4 gy-4', children}) => {
  if (!enabled) {
    return <>{children}</>
  }

  return <div className={className}>{children}</div>
}

export default ChannelSettingSequenceRow

